import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import { checkPaymentStatus } from '../services/paymentService';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'failed'
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get transaction ID from URL params
        const merchantTransactionId = searchParams.get('merchantTransactionId') || 
                                     searchParams.get('transactionId') ||
                                     searchParams.get('id');

        if (!merchantTransactionId) {
          setStatus('failed');
          setMessage('Invalid payment reference');
          return;
        }

        // Verify payment status with backend
        const result = await checkPaymentStatus(merchantTransactionId);

        if (result.success && result.data?.status === 'PAYMENT_SUCCESS') {
          setStatus('success');
          setMessage('Payment successful! Your plan has been activated.');
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            navigate('/employer/dashboard', {
              state: {
                paymentSuccess: true,
                message: 'Your plan has been activated successfully!'
              }
            });
          }, 3000);
        } else {
          setStatus('failed');
          setMessage(result.error || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setStatus('failed');
        setMessage('Failed to verify payment. Please contact support.');
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          {status === 'checking' && (
            <>
              <div className="flex justify-center mb-6">
                <FiLoader className="h-16 w-16 text-indigo-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Verifying Payment
              </h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="bg-green-100 rounded-full p-4">
                  <FiCheckCircle className="h-16 w-16 text-green-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="text-sm text-gray-500">
                Redirecting to dashboard...
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="bg-red-100 rounded-full p-4">
                  <FiXCircle className="h-16 w-16 text-red-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Payment Failed
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/pricing')}
                  className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/employer/dashboard')}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
